/**
 * `modules/action/modal.js`
 *  - 汎用モーダル制御
 *  - Vimeo動画の制御
 *  - 検索モーダルの制御
 *
 * 使用方法:
 *  - import {Modal, SearchModal, YouTubeModal, VimeoModal} from "../modules/action/modal.js";
 *    initModal(element, container, listeners);
 *    initSearchModal(element, container, listeners);
 *    initYouTubeModal(element, container, listeners);
 *    initVimeoModal(element, container, listeners);
 */


import {gsap} from "../gsap/gsap.js";
import {barbaEventObserver} from "../../core/barba/utils/barbaEventObserver.js";
import {lockScroll, unlockScroll} from "../../utils/index.js";


export class Modal {
  constructor(
    trigger,       // 1つのトリガー要素
    container,     // バルバ遷移時の対象コンテナ
    listeners = [], // 既存のイベントリスナー
    options = {}   // 追加オプション
  ) {
    this.trigger = trigger;
    this.modalId = trigger.dataset.modalId;
    this.modal = document.getElementById(this.modalId);
    this.listeners = listeners;
    this.options = options;
    this.contentSelector = ".modal_content";

    if (this.modal) {
      this.init();
    }
  }

  init() {
    const closeBtn = this.modal.querySelector("[data-modal='close']");
    const handleOpen = (event) => this.open(event);
    const handleClose = (event) => this.close(event);

    this.trigger.addEventListener("click", handleOpen);
    this.listeners.push([this.trigger, "click", handleOpen]);

    if (closeBtn) {
      closeBtn.addEventListener("click", handleClose);
      this.listeners.push([closeBtn, "click", handleClose]);
    }

    this.modal.addEventListener("click", (event) => {
      if (event.target === this.modal) {
        this.close(event);
      }
    });
    this.handleWheel = (event) => event.stopPropagation();

    barbaEventObserver.observeEvents(this.listeners);
  }

  open(event) {
    if (!this.modal) return;

    // スクロール管理
    lockScroll(); // 背景（本体）のスクロールを禁止する
    this.modal.addEventListener("wheel", this.handleWheel, { passive: false });
    this.listeners.push([this.modal, "wheel", this.handleWheel]); // `wheel` イベントを `this.listeners` に登録（モーダルを閉じずにページ遷移する場合を考慮）
    barbaEventObserver.observeEvents(this.listeners);

    // 表示
    const modalContent = this.modal.querySelector(this.contentSelector);
    gsap.fromTo(this.modal, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4, ease: "curtain" });
    gsap.fromTo(modalContent, { y: 80 }, { y: 0, duration: 0.8, ease: "btn" });
    this.modal.classList.add("is-open");

    if (this.options.onOpen) {
      this.options.onOpen();
    }
  }

  close(event) {
    if (!this.modal) return;

    // スクロール管理
    unlockScroll();  // 背景（本体）のスクロールを許可する
    // イベントリスナーを削除
    this.modal.removeEventListener("wheel", this.handleWheel, { passive: false });

    // `this.listeners` から `wheel` イベントを確実に削除（遷移時に再度remove処理が走ることを避ける）
    this.listeners = this.listeners.filter(([el, type, listener]) =>
      !(el === this.modal && type === "wheel" && listener === this.handleWheel)
    );

    // 非表示
    const modalContent = this.modal.querySelector(this.contentSelector);
    gsap.to(this.modal, { autoAlpha: 0, duration: 0.4, ease: "curtain" });
    gsap.to(modalContent, { y: -80, duration: 0.8, ease: "btn" });
    this.modal.classList.remove("is-open");

    if (this.options.onClose) {
      this.options.onClose();
    }
  }

  destroy() {
    barbaEventObserver.clearEvents(this.listeners);
    this.listeners = [];
  }
}


/**
 * 検索モーダル
 */
export class SearchModal extends Modal {
  constructor(trigger, container, listeners = [], options = {}) {
    super(trigger, container, listeners, options);
  }

  open(event) {
    super.open(event);
    const input = this.modal.querySelector("input");
    if (input) {
      input.focus();
    }
  }
}


/**
 * 動画再生モーダル (基底クラス)
 */
export class VideoModal extends Modal {
  constructor(trigger, container, listeners = [], options = {}) {
    super(trigger, container, listeners, options);
    this.videoElement = this.modal.querySelector("iframe");
    this.player = null;
  }

  async initPlayer() {
    throw new Error("Method 'initPlayer()' must be implemented.");
  }

  open(event) {
    this.initPlayer();
    super.open(event);
  }

  close(event) {
    super.close(event);
    if (this.player) {
      this.pauseVideo();
    }
  }

  pauseVideo() {
    throw new Error("Method 'pauseVideo()' must be implemented.");
  }
}


/**
 * YouTube 再生モーダル
 */
export class YouTubeModal extends VideoModal {
  constructor(trigger, container, listeners = [], options = {}) {
    super(trigger, container, listeners, options);
    this.videoId = trigger.dataset.videoId;
    this.videoContainer = null;
    this.player = null;
  }

  async initPlayer() {
    if (!this.videoContainer) {
      this.videoContainer = this.modal.querySelector(".modal_video");
    }

    if (!this.videoContainer) {
      console.error("⚠️ .modal_video の要素が見つかりません");
      return;
    }

    // 既存の iframe を削除（初期化のため）
    this.videoContainer.innerHTML = "";

    // YouTube IFrame API をロード
    await this.loadYouTubeAPI();

    // YouTube iframe を追加
    const iframe = document.createElement("iframe");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
    iframe.setAttribute("allowfullscreen", "true");
    iframe.src = `https://www.youtube.com/embed/${this.videoId}?enablejsapi=1&controls=0&modestbranding=1&rel=0&playsinline=1&autoplay=1`;
    this.videoContainer.appendChild(iframe);

    // プレイヤーを初期化
    this.player = new YT.Player(iframe, { // eslint-disable-line no-undef
      videoId: this.videoId,
      events: {
        onReady: (event) => {
          event.target.playVideo();
          iframe.classList.add("is-loaded");
        }
      },
    });
  }

  async loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        // 🎯 すでに YT が存在するなら、即解決
        console.log("✅ YouTube API は既にロード済み");
        resolve();
      } else {
        // 🎯 まだロードされていない場合のみ script を追加
        if (!document.querySelector("script[src='https://www.youtube.com/iframe_api']")) {
          console.log("🚀 YouTube API をロード開始...");
          const script = document.createElement("script");
          script.src = "https://www.youtube.com/iframe_api";
          script.onload = () => {
            window.onYouTubeIframeAPIReady = resolve;
          };
          document.body.appendChild(script);
        } else {
          console.log("⏳ YouTube API のロードを待機中...");
          window.onYouTubeIframeAPIReady = resolve;
        }
      }
    });
  }

  pauseVideo() {
    if (this.player && typeof this.player.pauseVideo === "function") {
      this.player.pauseVideo();
    } else {
      console.warn("⚠️ YouTube Player が未初期化または pauseVideo が呼び出せません");
    }
  }

  close(event) {
    super.close(event);

    // モーダルを閉じたときに iframe を削除してリセット
    if (this.videoContainer) {
      this.videoContainer.innerHTML = "";
    }
  }
}


/**
 * Vimeo 再生モーダル
 */
export class VimeoModal extends VideoModal {
  constructor(trigger, container, listeners = [], options = {}) {
    super(trigger, container, listeners, options);
  }

  async initPlayer() {
    if (!this.player) {
      await this.loadVimeoAPI();
      this.player = new Vimeo.Player(this.videoElement, { // eslint-disable-line no-undef
        id: this.videoElement.dataset.videoId,
        autoplay: true,
      });
    } else {
      this.player.play();
    }
  }

  async loadVimeoAPI() {
    return new Promise((resolve) => {
      if (window.Vimeo) {
        resolve();
      } else {
        const script = document.createElement("script");
        script.src = "https://player.vimeo.com/api/player.js";
        script.onload = resolve;
        document.body.appendChild(script);
      }
    });
  }

  pauseVideo() {
    if (this.player) {
      this.player.pause();
    }
  }
}


/**
 * 初期化処理をエクスポート
 */
export const initModal = (element, container, listeners) => {
  new Modal(element, container, listeners);
}

export const initSearchModal = (element, container, listeners) => {
  new SearchModal(element, container, listeners);
}

export const initYouTubeModal = (element, container, listeners) => {
  new YouTubeModal(element, container, listeners);
}

export const initVimeoModal = (element, container, listeners) => {
  new VimeoModal(element, container, listeners);
}
